// Keep configuration decisions separate from work performed by the setup runner.
export const PROCESS_TASK_KEYS = new Set([
  "company_initial_backfill", "labor_setup_started", "company_labor_standard_optimization",
  "company_earned_labor_backfill", "company_initial_forecast", "backfills_run",
]);
export const DATA_MODAL_KEYS = new Set([
  "company_initial_backfill", "company_earned_labor_backfill", "company_initial_forecast", "store_backfills",
]);

export function companyConfigurationTasks(tasks) {
  const result = tasks.filter((task) => !PROCESS_TASK_KEYS.has(task.key));
  const finalKeys = new Set(["training_content_started", "billing_reviewed"]);
  return [...result.filter((task) => !finalKeys.has(task.key)), ...result.filter((task) => finalKeys.has(task.key))];
}

export const JOURNEY_STEPS = [
  { id: "history", label: "Historical data", stages: ["preflight_pos_sync", "optimizer_pos_extension", "history_pos_extension", "preflight_actual_sales_drivers", "preflight_actual_used_labor", "optimizer_actual_sales_drivers", "optimizer_actual_used_labor", "history_actual_sales_drivers", "history_actual_used_labor", "weather_backfill"] },
  { id: "model", label: "Labor model", stages: ["preflight_location_analysis", "optimize_labor_standards", "publish_labor_standards"] },
  { id: "outputs", label: "Forecasts and earned labor", stages: ["earned_labor", "forecasted_sales_drivers", "forecasted_labor", "archive_inactive_employees", "autoscheduling_recommendations"] },
];

const redundant = new Set(["optimizer_pos_extension", "optimizer_actual_sales_drivers", "optimizer_actual_used_labor", "history_pos_extension", "history_actual_sales_drivers", "history_actual_used_labor"]);
const phaseOrder = ["history", "model", "outputs"];
export const preparationStepLabels = {
  preflight_pos_sync: "Import POS history", preflight_actual_sales_drivers: "Calculate actual sales",
  preflight_actual_used_labor: "Calculate used labor", weather_backfill: "Load weather",
  optimizer_pos_extension: "Extend POS history for labor modeling",
  history_pos_extension: "Import remaining POS history",
  optimizer_actual_sales_drivers: "Calculate sales for labor modeling",
  optimizer_actual_used_labor: "Calculate used labor for labor modeling",
  history_actual_sales_drivers: "Calculate remaining historical sales",
  history_actual_used_labor: "Calculate remaining historical used labor",
  preflight_location_analysis: "Check locations", optimize_labor_standards: "Build initial labor model",
  publish_labor_standards: "Publish labor model", earned_labor: "Calculate earned labor",
  forecasted_sales_drivers: "Forecast sales", forecasted_labor: "Forecast labor",
  archive_inactive_employees: "Archive inactive employees", autoscheduling_recommendations: "Prepare scheduling recommendations",
};
export function journeySteps(run, storeId) {
  return JOURNEY_STEPS.map((group, index) => {
    const keys = group.stages.filter((key) => (run && run.preset_version !== "full_history_v1") || !redundant.has(key));
    const target = run?.preparation_phase || "outputs";
    const requested = run?.result_summary?.requested_phase || target;
    const unrequested = run?.preset_version === "full_history_v1" && phaseOrder.indexOf(target) < index;
    const rows = keys.map((key) => {
      const stage = (run?.stages || []).find((stage) => stage.stage_key === key);
      const items = (run?.items || []).filter((item) => item.stage_key === key && (storeId == null || item.store_id == null || String(item.store_id) === String(storeId)));
      const done = items.filter((item) => ["completed", "not_applicable"].includes(item.status)).length;
      const status = !run ? "not_started" : unrequested ? "not_started" : storeId == null ? stage?.status || "pending"
        : !items.length ? stage?.status === "not_applicable" ? "not_applicable" : "pending"
        : done === items.length ? "completed" : items.some((item) => item.status === "failed") ? "failed"
        : items.some((item) => item.status === "running") ? "running" : "pending";
      return { key, label: preparationStepLabels[key] || stage?.label || key.replaceAll("_", " "), status,
        completed: unrequested ? 0 : done, total: items.length };
    });
    const done = rows.filter((row) => ["completed", "not_applicable"].includes(row.status)).length;
    const status = !run ? "not_started" : unrequested ? phaseOrder.indexOf(requested) >= index ? "queued" : "not_started"
      : done === keys.length ? "completed" : rows.some((row) => row.status === "failed") ? "failed"
      : group.id === "model" && run?.status === "awaiting_location_review" ? "review"
      : rows.some((row) => row.status === "running") ? "running" : "waiting";
    return { ...group, stages: keys, rows, status, completed: done, total: keys.length };
  });
}
export function locationJourney(run, storeId) { return journeySteps(run, storeId); }

// Read-only evidence from the existing pipeline ledger. Never manufacture an
// onboarding run or let a short initial import satisfy full-history preparation.
export function pipelineHistory(store) {
  const candidates = (store?.dataPreparationProgress || []).filter((run) =>
    run.source === "pipeline_run" && run.requestedOperations?.include_pos_sync);
  const source = candidates.sort((a, b) => (b.lastActivityAt || "").localeCompare(a.lastActivityAt || ""))[0];
  if (!source) return null;
  const steps = source.steps || [];
  const ranges = steps.filter((step) => step.taskKey === "run_store_backfill" && step.dateRange)
    .map((step) => [Date.parse(step.dateRange.start), Date.parse(step.dateRange.end) + 86400000])
    .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start)
    .sort((a, b) => a[0] - b[0]);
  let covered = 0;
  let lastEnd = -Infinity;
  for (const [start, end] of ranges) {
    covered += Math.max(0, end - Math.max(start, lastEnd));
    lastEnd = Math.max(lastEnd, end);
  }
  const fullHistory = covered >= 455 * 86400000;
  const groups = [
    ["preflight_pos_sync", ["run_store_backfill"]],
    ["preflight_actual_sales_drivers", ["run_pos_actual_sales", "run_onboarding_sales_driver_backfill"]],
    ["preflight_actual_used_labor", ["run_store_actual_labor_backfill"]],
    ["weather_backfill", ["get_weather_data"]],
  ];
  const rows = groups.map(([key, names]) => {
    const tasks = steps.filter((step) => names.includes(step.taskKey));
    const status = !tasks.length || !fullHistory || (key === "preflight_actual_sales_drivers" && !tasks.some((task) => task.taskKey === "run_onboarding_sales_driver_backfill")) ? "not_started"
      : tasks.some((task) => task.status === "failed") || source.status === "failed" ? "failed"
      : tasks.every((task) => task.status === "completed") ? "completed"
      : tasks.some((task) => task.status === "running") ? "running" : "pending";
    return { key, label: preparationStepLabels[key], status, sourceRunId: source.id };
  });
  const completed = rows.filter((row) => row.status === "completed").length;
  return { ...JOURNEY_STEPS[0], rows, completed, total: rows.length,
    sourceRunId: source.id, lastActivityAt: source.lastActivityAt,
    dateRange: ranges.length ? { start: new Date(ranges[0][0]).toISOString().slice(0, 10), end: new Date(lastEnd - 86400000).toISOString().slice(0, 10) } : null,
    status: completed === rows.length && source.status === "completed" ? "completed"
      : rows.some((row) => row.status === "failed") ? "failed"
      : rows.some((row) => row.status === "running") ? "running" : "not_started" };
}

export function displayedJourney(run, stores = [], storeId) {
  const steps = journeySteps(run, storeId);
  // A real orchestrator run remains authoritative when present.
  if (run) return steps;
  const selected = storeId == null ? stores : stores.filter((store) => String(store.store_id) === String(storeId));
  const histories = selected.map((store) => pipelineHistory(store));
  if (!histories.some(Boolean)) return steps;
  if (storeId != null) {
    if (histories[0]) steps[0] = histories[0];
  } else {
    const completed = histories.reduce((sum, history) => sum + (history?.completed || 0), 0);
    steps[0] = { ...steps[0], completed, total: selected.length * 4,
      status: histories.every((history) => history?.status === "completed") ? "completed"
        : histories.some((history) => history?.status === "failed") ? "failed"
        : histories.some((history) => history?.status === "running") ? "running" : "not_started" };
  }
  return steps;
}
